import styled from 'styled-components';

/** A row is a 4-unit grid; the container decides how many units each metric spans. */
export const STATISTICS_ROW_UNITS = 4;
/** A narrow metric (Substantiation Rate, Undeliverable Mail, Fraud Review) always spans one unit. */
export const STATISTICS_NARROW_SPAN = 1;

/**
 * One line of the section: a 4-unit CSS grid. The container pairs up to two narrow metrics with a
 * wide metric per row and stretches the wide metric to fill the row's remaining units, so a wide
 * metric always occupies all the free space beside its narrow metrics:
 * - 2 narrow + 1 wide -> 1fr 1fr 2fr
 * - 1 narrow + 1 wide -> 1fr 3fr
 * - 1 wide alone      -> 4fr (full width)
 * Below the container breakpoint the row collapses to a single column and the cards stack.
 */
export const StatisticsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(${STATISTICS_ROW_UNITS}, minmax(0, 1fr));
  align-items: stretch;
  gap: ${({ theme }) => theme.spacings.spacing12};
  width: 100%;

  @container dashboard-statistics (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

interface StatisticsCellProps {
  /** How many of the row's 4 units this metric spans. */
  $span: number;
}

const StatisticsCell = styled.div<StatisticsCellProps>`
  display: flex;
  min-width: 0;
  grid-column: span ${({ $span }) => $span};

  & > * {
    flex: 1;
    min-width: 0;
  }

  /* Stacked layout: every card spans the single column. */
  @container dashboard-statistics (max-width: 900px) {
    grid-column: 1 / -1;
  }
`;

/** Narrow metrics span a single unit. */
export const NarrowStatistic = styled(StatisticsCell)``;

/** Wide metrics stretch to fill their row's remaining units (the container sets the span). */
export const WideStatistic = styled(StatisticsCell)``;

/**
 * Stacks the rows of the section and acts as the query container the rows measure themselves
 * against, so the layout reacts to the section's own width rather than the viewport width.
 */
export const StatisticsGrid = styled.div`
  container-type: inline-size;
  container-name: dashboard-statistics;
  display: grid;
  gap: ${({ theme }) => theme.spacings.spacing12};
  width: 100%;
`;
